import { useState, useCallback } from 'react'
import { Outlet } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { CabinetTopBar } from './CabinetTopBar'
import { PageHeaderProvider } from './PageHeaderProvider'
import { SessionManagerPrompt } from './SessionManagerPrompt'
import { AppUpdateBanner } from '@/components/common/AppUpdateBanner'
import { TranscriptDialogHost } from '@/components/dashboard/TranscriptDialogHost'
import { TutorialButton } from '@/components/tutorials/TutorialButton'
import { useCurrentTutorial } from '@/components/tutorials/useCurrentTutorial'
import { OnboardingButton } from '@/components/tutorials/OnboardingButton'
import { useOnboardingDismissed } from '@/components/tutorials/useOnboardingDismissed'
import { bottomGutterClass } from '@/components/tutorials/onboardingPlacement'
import { cn } from '@/lib/utils'
import { BottomGutterContext } from './bottomGutter'

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  // KAN-950/952: bottom-right tutorial and onboarding buttons get room under
  // the content, so pagination, table ends and input rows stay clickable.
  const tutorial = useCurrentTutorial()
  const [onboardingDismissed] = useOnboardingDismissed()
  // KAN-957: страница, которая сама тянется до низа (доска CRM), снимает нижний отступ
  // целиком (pb-0): снизу остаётся только отступ самой страницы.
  const [gutterReleases, setGutterReleases] = useState(0)
  const releaseGutter = useCallback(() => {
    setGutterReleases((n) => n + 1)
    return () => setGutterReleases((n) => n - 1)
  }, [])
  const tutorialGutter =
    gutterReleases > 0 ? 'pb-0' : bottomGutterClass(tutorial?.placement, !onboardingDismissed)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === 'true'
  )

  const handleToggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev)
  }, [])

  const handleCloseSidebar = useCallback(() => {
    setSidebarOpen(false)
  }, [])

  const handleToggleCollapse = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev
      localStorage.setItem('sidebarCollapsed', String(next))
      return next
    })
  }, [])

  // KAN-961: заголовок страницы (PageLayout) показывает верхняя строка.
  return (
    <PageHeaderProvider>
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={handleCloseSidebar}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleCollapse}
      />
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Mobile menu button; KAN-753: session manager indicator */}
        <CabinetTopBar onToggleSidebar={handleToggleSidebar} />
        {/* KAN-870: new build deployed, offer reload. */}
        <AppUpdateBanner />
        <main
          className={cn('flex-1 overflow-y-auto p-6 focus:outline-none', tutorialGutter)}
          // KAN-952: focus target after the "С чего начать" cross when no page button.
          tabIndex={-1}
          data-testid="dashboard-main"
        >
          <BottomGutterContext.Provider value={releaseGutter}>
            <Outlet />
          </BottomGutterContext.Provider>
        </main>
        {/* KAN-710: the client conversation modal follows ?dialog=<clientId>. */}
        <TranscriptDialogHost />
        {/* KAN-753: who works with clients in this session (once per login). */}
        <SessionManagerPrompt />
        {/* KAN-950: floating video tutorial for the current page. */}
        <TutorialButton />
        {/* KAN-952: "С чего начать" until dismissed in this browser. */}
        <OnboardingButton />
      </div>
    </div>
    </PageHeaderProvider>
  )
}
